<template>
    <div class="text-sm">
        <p v-if="result.error" class="text-taupe-600">
            Unable to compute a field-level diff —
            {{
                result.error === 'invalid-old'
                    ? 'the previous version'
                    : 'this version'
            }}
            is not valid JSON.
        </p>

        <p v-else-if="result.changes.length === 0" class="text-taupe-500">
            No changes to this value.
        </p>

        <div v-else class="space-y-1.5">
            <div
                v-for="(change, index) in result.changes"
                :key="`${change.path}-${index}`"
                class="flex flex-wrap items-start gap-x-2 gap-y-0.5 rounded-md border px-2.5 py-1.5"
                :class="rowClasses(change.type)"
            >
                <span
                    class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="badgeClasses(change.type)"
                >
                    {{ typeLabel(change.type) }}
                </span>
                <span class="font-mono text-xs text-taupe-700">{{
                    change.path
                }}</span>

                <template v-if="change.type === 'changed'">
                    <span class="font-mono text-xs text-red-700 line-through">
                        {{ formatChangeValue(change.oldValue) }}
                    </span>
                    <span class="text-taupe-400">&rarr;</span>
                    <span class="font-mono text-xs text-emerald-700">
                        {{ formatChangeValue(change.newValue) }}
                    </span>
                </template>
                <span
                    v-else-if="change.type === 'added'"
                    class="font-mono text-xs text-emerald-700"
                >
                    {{ formatChangeValue(change.newValue) }}
                </span>
                <span
                    v-else
                    class="font-mono text-xs text-red-700 line-through"
                >
                    {{ formatChangeValue(change.oldValue) }}
                </span>
            </div>

            <p v-if="result.truncated" class="text-xs text-taupe-500">
                Showing the first {{ JSON_DIFF_CHANGE_LIMIT }} changes.
            </p>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { diffJson, JSON_DIFF_CHANGE_LIMIT } from '@/utils/json-diff'
import { truncateDisplay } from '@/utils/formatters'

const props = defineProps({
    // Nullable: `null` means "no prior version" (e.g. the oldest history
    // entry), which diffJson() treats as every path being added.
    /** @type {import('vue').PropType<string|null>} */
    oldValue: {
        type: String,
        default: null,
    },
    /** @type {import('vue').PropType<string|null>} */
    newValue: {
        type: String,
        default: null,
    },
})

const result = computed(() => diffJson(props.oldValue, props.newValue))

const CHANGE_VALUE_MAX_LENGTH = 160

// Always JSON-encoded (unlike truncateDisplay's usual whole-document
// callers, which pass already-stringified JSON through as-is) so a leaf
// value's type stays visible in the diff — e.g. the string "v1" reads as
// `"v1"`, not indistinguishably from a bare v1 token.
function formatChangeValue(value) {
    if (value === undefined) {
        return 'undefined'
    }

    return truncateDisplay(JSON.stringify(value), CHANGE_VALUE_MAX_LENGTH)
}

function typeLabel(type) {
    if (type === 'added') return 'Added'
    if (type === 'removed') return 'Removed'
    return 'Changed'
}

function rowClasses(type) {
    if (type === 'added') return 'border-emerald-200 bg-emerald-50'
    if (type === 'removed') return 'border-red-200 bg-red-50'
    return 'border-amber-200 bg-amber-50'
}

function badgeClasses(type) {
    if (type === 'added')
        return 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200'
    if (type === 'removed') return 'bg-red-100 text-red-700 ring-1 ring-red-200'
    return 'bg-amber-100 text-amber-700 ring-1 ring-amber-200'
}
</script>
